// The browser's copy of what it was given (slice 04, issue 744): a projected
// state, moved forward by projected events. It holds only what the server
// projected for this person — a secret is not in it to be found — and it
// notices a dropped message by the per-connection counter, at which point
// the page asks for a fresh snapshot rather than guessing (plan §2.5).
import {
  defaultGrid, defaultScene, emptyEncounter,
  applyEvent, applyProjected, parseEnvelope, type EventBody, type ProjectedEvent, type ProjectedState, type Role, type TableState,
} from '@dragons-table/vtt-shared';

export interface Store {
  /** The projection this person holds. For the DM it is the whole state, hidden flagged. */
  state: ProjectedState;
  /** The last counter received; the next envelope must carry n + 1. */
  n: number;
  /** How many rewinds this store has taken: the state jumped, so what changed is not news (the review of 2026-10-18). */
  rewinds?: number;
}

export const emptyStore = (): Store => ({ state: { objects: {}, grid: defaultGrid(), scene: defaultScene(), encounter: emptyEncounter(), focuses: {}, areas: {}, drawings: [] }, n: 0 });

export function fromSnapshot(state: ProjectedState, n: number): Store {
  // A snapshot from a server older than the turn order (slice 12) carries none: nothing running.
  return { state: { ...state, encounter: state.encounter ?? emptyEncounter(), focuses: state.focuses ?? {}, areas: state.areas ?? {}, drawings: state.drawings ?? [] }, n };
}

/** Apply an envelope. `gap` says the counter skipped: the caller re-syncs and ignores the store returned. */
export function receive(store: Store, raw: unknown, role: Role, meId?: string): { store: Store; gap: boolean; event?: ProjectedEvent } {
  const env = parseEnvelope(raw);
  if (!env) return { store, gap: false };
  if (env.n !== store.n + 1) return { store, gap: true };
  const event = env.event as ProjectedEvent;
  if (event.kind === 'dm.event') {
    // The DM's projection is the whole state, so the DM reduces the body itself — as the DM's own
    // action when it was theirs, which is what clears the undelete (design/12 §5). The frame names the role the server
    // reduced it with (a co-DM's DM act is the DM's); a frame without it falls back to "mine, or a player's".
    if (role !== 'dm') return { store: { ...store, n: env.n }, gap: false };
    const acting: Role = event.role ?? (event.actorId === meId ? 'dm' : 'player');
    const next = applyEvent(store.state as unknown as TableState, event.body as EventBody, acting);
    return { store: { ...store, state: next as unknown as ProjectedState, n: env.n, ...(event.body.kind === 'scene.rewind' ? { rewinds: (store.rewinds ?? 0) + 1 } : {}) }, gap: false, event };
  }
  return { store: { ...store, state: applyProjected(store.state, event), n: env.n, ...(event.kind === 'table.rewind' || event.kind === 'table.floors' ? { rewinds: (store.rewinds ?? 0) + 1 } : {}) }, gap: false, event };
}
